import type { MessagingMessage } from '../../../api/generated/model/messagingMessage'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'
import { formatTime } from '../../../lib/datetime'

export type MessagingSurface = 'admin' | 'seller' | 'supply'
export type MessageDeliveryState = 'sent' | 'pending' | 'failed'

type MessageBubbleProps = {
  message: MessagingMessage
  surface: MessagingSurface
  deliveryState?: MessageDeliveryState
  onRetry?: () => void
  counterpartLabel?: string
  appearance?: 'default' | 'messenger'
}

function isOwnMessage(message: MessagingMessage, surface: MessagingSurface) {
  return surface === 'admin'
    ? message.author_kind === 'pexify_user'
    : message.author_kind === 'app_user'
}

export function MessageBubble({
  message,
  surface,
  deliveryState = 'sent',
  onRetry,
  counterpartLabel,
  appearance = 'default',
}: MessageBubbleProps) {
  const t = useT(messagingI18n)

  if (message.kind === 'internal_note' && surface !== 'admin') {
    return null
  }

  const own = isOwnMessage(message, surface)
  const internal = message.kind === 'internal_note'
  const system = message.kind === 'system' || message.author_kind === 'system'
  const author = system
    ? t.states.system
    : own
      ? t.states.you
      : message.author_kind === 'pexify_user'
        ? t.states.pexify
        : surface === 'admin'
          ? (counterpartLabel ?? t.composer.reply)
          : t.states.pexify
  const body = message.retracted_at ? t.states.removed : message.body

  if (system) {
    return (
      <div className="my-3 text-center text-xs text-[var(--color-text-muted)]" role="status">
        {body}
      </div>
    )
  }

  return (
    <article
      data-testid="message-bubble"
      data-own={String(own)}
      data-message-side={own ? 'self' : 'counterpart'}
      data-appearance={appearance}
      className={`flex ${own ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[min(30rem,82%)] border px-3.5 py-2.5 shadow-sm ${
          appearance === 'messenger'
            ? `rounded-[18px] ${
                internal
                  ? 'border-amber-300/25 bg-amber-300/10 text-amber-50'
                  : own
                    ? 'rounded-br-md border-white/80 bg-white text-[#12161d]'
                    : 'rounded-bl-md border-white/5 bg-[#272b33] text-white'
              }`
            : `rounded-2xl ${
                internal
                  ? 'border-amber-200 appearance-dark:border-[var(--color-border)] bg-amber-50 appearance-dark:bg-[var(--color-surface-muted)] text-amber-950 appearance-dark:text-[var(--color-text)]'
                  : own
                    ? 'border-[var(--color-action)] bg-[var(--color-action)] text-[var(--color-action-foreground)]'
                    : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]'
              }`
        }`}
      >
        <div
          className={`mb-1 flex items-center gap-2 text-[11px] font-semibold ${
            appearance === 'messenger'
              ? own && !internal
                ? 'text-[#6f7681]'
                : 'text-white/55'
              : own && !internal
                ? 'text-white/70 appearance-dark:text-[var(--color-action-foreground)]'
                : 'text-[var(--color-text-muted)]'
          }`}
        >
          <span>{internal ? t.composer.internalNote : author}</span>
          {appearance === 'messenger' ? (
            <time dateTime={message.created_at}>
              {formatTime(message.created_at, {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </time>
          ) : null}
          {deliveryState === 'pending' ? <span>{t.states.pending}</span> : null}
        </div>
        <p
          className={`whitespace-pre-wrap break-words text-sm leading-5 ${
            message.retracted_at ? 'italic opacity-75' : ''
          }`}
        >
          {body}
        </p>
        {deliveryState === 'failed' ? (
          <div className="mt-2 flex items-center gap-2 text-xs" role="alert">
            <span>{t.composer.failed}</span>
            {onRetry ? (
              <button
                type="button"
                className="font-semibold underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
                onClick={onRetry}
              >
                {t.composer.retry}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  )
}
